import * as React from 'react';
import styled, { css } from 'styled-components';

import { Popover } from '../../composites/Popover/Popover';
import { Checkbox } from '../../primitives/Checkbox/Checkbox';
import { Button } from '../../primitives/Button/Button';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';
import type { FloatingAlign, FloatingSide } from '../../internals/computeFloatingPosition';

/* ─────────────────────────── Types ─────────────────────────────────────── */

export interface ColumnOption {
  label: string;
  value: string;
}

/** Legacy automation DOM hooks for table-builder column picker XPaths. */
export interface ColumnPickerLegacyCompat {
  /** Sets the trigger button id (e.g. `dropdownId`). V2 semantic id stays on data-testid. */
  triggerId?: string;
  /** Appended to the panel container className (e.g. `dropdownListContainer`). */
  panelClassName?: string;
  /** Renders checkbox in a preceding-sibling div and label text in div.cellTitle. */
  useLegacyRowDom?: boolean;
}

export interface ColumnPickerProps {
  columns: ColumnOption[];
  checkedValues: string[];
  onSave: (values: string[]) => void;
  triggerLabel?: string;
  triggerIcon?: string;
  title?: string;
  loading?: boolean;
  side?: FloatingSide;
  align?: FloatingAlign;
  semanticId?: string;
  legacyCompat?: ColumnPickerLegacyCompat;
}

/* ─────────────────────────── Trigger button ─────────────────────────────── */

const TriggerBtn = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  min-height: ${rem(34)};
  padding: ${rem(8)} ${rem(10)};
  border-radius: var(--ui-radius-md, ${rem(6)});
  border: 1.5px solid
    ${({ $open }) =>
      $open
        ? 'var(--ui-interactive, ' + palette.Purple + ')'
        : 'var(--ui-border, ' + palette.LightGray + ')'};
  background: ${({ $open }) =>
    $open
      ? 'var(--ui-interactive-light, ' + palette.LightPurple + ')'
      : 'var(--ui-surface, ' + palette.White + ')'};
  color: ${({ $open }) =>
    $open
      ? 'var(--ui-interactive, ' + palette.Purple + ')'
      : 'var(--ui-text-muted, ' + palette.Gray + ')'};
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  font-size: ${rem(14)};
  font-weight: 500;
  line-height: ${rem(16)};
  cursor: pointer;
  transition: border-color 0.18s, background 0.18s, color 0.18s;
  white-space: nowrap;

  &:hover {
    border-color: var(--ui-interactive, ${palette.Purple});
    color: var(--ui-interactive, ${palette.Purple});
    background: var(--ui-interactive-light, ${palette.LightPurple});
  }

  &:focus-visible {
    outline: 2px solid var(--ui-interactive, ${palette.Purple});
    outline-offset: 2px;
  }

  .cp-icon {
    font-size: ${rem(16)};
    line-height: 1;
  }

  .cp-chevron {
    font-size: ${rem(14)};
    margin-left: ${rem(2)};
    opacity: 0.6;
    transition: transform 0.2s;
    transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0)')};
  }
`;

/* ─────────────────────────── Panel styled ───────────────────────────────── */

const PanelContainer = styled.div`
  width: min(${rem(300)}, calc(100vw - ${rem(32)}));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: var(--ui-radius-lg, ${rem(12)});
`;

const PanelHeader = styled.div`
  padding: ${rem(14)} ${rem(18)} ${rem(12)};
  border-bottom: 1px solid var(--ui-border, ${palette.CulturedWhite});
`;

const PanelTitle = styled.span`
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  font-size: ${rem(11)};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  line-height: ${rem(16)};
`;

const ListScroll = styled.div`
  max-height: ${rem(320)};
  overflow-y: auto;
  padding: ${rem(4)} 0;

  &::-webkit-scrollbar {
    width: ${rem(5)};
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: ${rem(7)};
  }
  &::-webkit-scrollbar-thumb {
    background: var(--ui-text-subtle, ${palette.SpanishGray});
    border-radius: ${rem(7)};
    &:hover {
      background: #555;
    }
  }
`;

/** Compact DS Checkbox control sizing inside column picker rows (15px). */
const columnPickerCheckboxControlCompact = css`
  width: ${rem(15)};
  height: ${rem(15)};

  & > input[type='checkbox'] {
    min-width: ${rem(15)};
    min-height: ${rem(15)};
  }

  &::before {
    width: ${rem(15)};
    height: ${rem(15)};
  }

  &:has(> input:checked)::before {
    border-color: var(--ui-interactive, ${palette.Purple});
  }

  &:has(> input:checked)::after {
    width: ${rem(15)};
    height: ${rem(15)};
    font-size: ${rem(14)};
    line-height: ${rem(15)};
    background-color: var(--ui-interactive, ${palette.Purple});
  }
`;

const columnPickerRowCheckedBg = css`
  background: var(--ui-interactive-light, ${palette.LightPurple});
`;

const OptionRow = styled.div<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  padding: ${rem(10)} ${rem(18)};
  cursor: pointer;
  transition: background 0.12s ease;
  user-select: none;

  ${({ $checked }) =>
    $checked
      ? columnPickerRowCheckedBg
      : css`
          background: transparent;
        `}

  &:hover {
    background: var(--ui-background-muted, ${palette.OffWhite});
  }

  /* Compact DS Checkbox inside column rows */
  & > div {
    gap: ${rem(10)};
    min-height: unset;
    min-width: unset;
  }

  & > div > span {
    ${columnPickerCheckboxControlCompact}
  }

  & label {
    font-size: ${rem(14)};
    line-height: ${rem(20)};
    font-weight: 400;
    color: var(--ui-text, ${palette.Black});
  }
`;

const RowDivider = styled.div`
  height: 1px;
  background: var(--ui-border, ${palette.CulturedWhite});
`;

const PanelFooter = styled.div`
  padding: ${rem(12)} ${rem(18)};
  border-top: 1px solid var(--ui-border, ${palette.CulturedWhite});
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem(8)};
`;

const EmptyPanel = styled.div`
  padding: ${rem(32)} ${rem(16)};
  text-align: center;
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  font-size: ${rem(13)};

  .material-symbols-outlined {
    font-size: ${rem(32)};
    opacity: 0.35;
    display: block;
    margin-bottom: ${rem(8)};
  }
`;

/** Legacy automation row: div(checkbox) + label(div.cellTitle) siblings. */
const LegacyCheckboxDiv = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: ${rem(10)};
`;

const LegacyOptionRow = styled.div<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  padding: ${rem(10)} ${rem(18)};
  cursor: pointer;
  transition: background 0.12s ease;
  user-select: none;

  ${({ $checked }) =>
    $checked
      ? columnPickerRowCheckedBg
      : css`
          background: transparent;
        `}

  &:hover {
    background: var(--ui-background-muted, ${palette.OffWhite});
  }

  ${LegacyCheckboxDiv} > div {
    gap: 0;
    min-height: unset;
    min-width: unset;
  }

  ${LegacyCheckboxDiv} span {
    ${columnPickerCheckboxControlCompact}
  }
`;

const LegacyLabel = styled.label`
  margin: 0;
  flex: 1;
  cursor: pointer;
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  font-size: ${rem(14)};
  line-height: ${rem(20)};
  font-weight: 400;
  color: var(--ui-text, ${palette.Black});
`;

/* ─────────────────────────── Component ─────────────────────────────────── */

export const ColumnPicker: React.FC<ColumnPickerProps> = ({
  columns,
  checkedValues,
  onSave,
  triggerLabel = 'Columns',
  triggerIcon = 'view_column',
  title = 'Manage Columns',
  loading = false,
  side = 'bottom',
  align = 'start',
  semanticId = 'column-picker',
  legacyCompat,
}) => {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (open) setDraft([...checkedValues]);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevLoading = React.useRef(loading);
  React.useEffect(() => {
    if (prevLoading.current && !loading) setOpen(false);
    prevLoading.current = loading;
  }, [loading]);

  const toggle = React.useCallback((value: string) => {
    setDraft((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  }, []);

  const handleSave = React.useCallback(() => {
    onSave(draft);
  }, [draft, onSave]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen} side={side} align={align}>
      <Popover.Trigger asChild>
        <TriggerBtn
          type='button'
          $open={open}
          id={legacyCompat?.triggerId ?? `${semanticId}-trigger`}
          data-testid={`${semanticId}-trigger`}>
          <span className='material-symbols-outlined cp-icon'>{triggerIcon}</span>
          {triggerLabel}
          <span className='material-symbols-outlined cp-chevron'>expand_more</span>
        </TriggerBtn>
      </Popover.Trigger>

      <Popover.Content semanticId={`${semanticId}-panel`} data-testid={`${semanticId}-panel`}>
        <PanelContainer className={legacyCompat?.panelClassName}>
          <PanelHeader>
            <PanelTitle>{title}</PanelTitle>
          </PanelHeader>

          {columns.length === 0 ? (
            <EmptyPanel>
              <span className='material-symbols-outlined'>table_chart</span>
              No optional columns
            </EmptyPanel>
          ) : (
            <>
              <ListScroll>
                {columns.map((col, idx) => {
                  const isChecked = draft.includes(col.value);
                  const inputId = `${semanticId}-col-${col.value}`;

                  if (legacyCompat?.useLegacyRowDom) {
                    return (
                      <React.Fragment key={col.value}>
                        <LegacyOptionRow
                          $checked={isChecked}
                          onClick={() => toggle(col.value)}
                          role='option'
                          aria-selected={isChecked}
                          data-testid={`${semanticId}-row-${col.value}`}>
                          <LegacyCheckboxDiv>
                            <Checkbox
                              id={inputId}
                              checked={isChecked}
                              readOnly
                              onChange={() => {}}
                              semanticId={`${semanticId}-col-${col.value}`}
                            />
                          </LegacyCheckboxDiv>
                          <LegacyLabel>
                            <div className='cellTitle'>{col.label}</div>
                          </LegacyLabel>
                        </LegacyOptionRow>
                        {idx < columns.length - 1 && <RowDivider />}
                      </React.Fragment>
                    );
                  }

                  return (
                    <React.Fragment key={col.value}>
                      <OptionRow
                        $checked={isChecked}
                        onClick={() => toggle(col.value)}
                        role='option'
                        aria-selected={isChecked}
                        data-testid={`${semanticId}-row-${col.value}`}>
                        <Checkbox
                          id={inputId}
                          checked={isChecked}
                          onChange={() => toggle(col.value)}
                          label={col.label}
                          semanticId={`${semanticId}-col-${col.value}`}
                        />
                      </OptionRow>
                      {idx < columns.length - 1 && <RowDivider />}
                    </React.Fragment>
                  );
                })}
              </ListScroll>

              <PanelFooter>
                <Button
                  type='button'
                  variant='primary'
                  size='sm'
                  icon='check'
                  label={loading ? 'Saving…' : 'Save'}
                  loading={loading}
                  disabled={loading}
                  onClick={handleSave}
                  semanticId={`${semanticId}-save`}
                  data-testid={`${semanticId}-save`}
                />
              </PanelFooter>
            </>
          )}
        </PanelContainer>
      </Popover.Content>
    </Popover.Root>
  );
};

ColumnPicker.displayName = 'ColumnPicker';
